/**
 * Catches render-time crashes.
 *
 * Without one, any exception inside a component unmounts the whole tree and the
 * user gets a blank white page — no message, no way back, nothing to report.
 * That is the one outcome design.md §5 does not allow: every failure has to say
 * something. React has no hook equivalent, so this stays a class component.
 */

import { AlertTriangle, RefreshCw } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'

interface Props {
  children: React.ReactNode
  /** Changing this resets the boundary — used to clear the error on navigation. */
  resetKey?: string
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(previous: Props) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep it in the console for the developer; the user gets the panel below.
    console.error('Unhandled render error', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex min-h-full items-center justify-center bg-bg px-6 py-16">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 text-center">
          <div className="mx-auto flex size-10 items-center justify-center rounded-lg bg-danger-soft text-danger">
            <AlertTriangle className="size-5" />
          </div>
          <p className="mt-3 text-sm font-medium">This screen stopped working</p>
          <p className="mt-1 text-xs text-muted">
            Something in the page failed to render. Reloading usually clears it; if it
            keeps happening, the message below identifies the fault.
          </p>
          <pre className="mt-3 overflow-x-auto rounded-md border border-border-soft bg-surface-2 px-3 py-2 text-left font-mono text-[11px] text-muted">
            {this.state.error.message}
          </pre>
          <div className="mt-4 flex justify-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
              <RefreshCw />
              Reload
            </Button>
            <Button size="sm" variant="ghost" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
